import { Observable } from "rxjs";
import HttpClient from "@/api/HttpClient";
import { APIHosts, ApiReduxHelpers } from "@/services/reduxHelpers";

export type FileListItemDto = {
  fileId: number;
  fileName: string;
  mimeType?: string | null;
  sizeBytes?: number | null;
  currentVersion: number;
  updatedAt: string;
  isDeleted: boolean;
};

export interface FileApiResponse {
  results: FileListItemDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export type InitFileRequest = { platformId?: number | null };
export type InitFileResponse = { fileId: number };

export type CompleteVersionRequest = {
  fileName: string;
  mimeType?: string | null;
  sizeBytes?: number | null;
  bucketName: string;
  objectKey: string;
  versionId?: string | null;
  eTag?: string | null;
  crc?: string | null;
};

export type CompleteVersionResponse = {
  fileId: number;
  versionRowId: number;
  versionNumber: number;
};

export type FileExistsResponse = {
  exists: boolean;
  fileId?: number;
  currentVersion?: number;
  version?: string;
  document?: {
    id?: string;
    fileId?: string;
    filename?: string;
    size?: number;
    mimeType?: string;
    cloudLink?: string;
    bucketName?: string;
    status?: string;
    createdAt?: string;
    version?: string;
  };
  message?: string;
};

export type FileVersionDto = {
  fileId: string;
  fileCode: string;
  name: string;
  type: string;
  jsonContent: string;
  isPublish: boolean;
  parentId: string;
  version: string;
  size: number;
  createDate: string;
  lastModified: string;
  owner: {
    name: string;
    icon: string;
    id: string;
    email: string;
  };
  id: string;
  cloudLink: string;
  typeStorage: string;
  clientId: string;
  bucketName: string;
  isDeleted: boolean;
  isActive?: boolean;
};

export type FilterConditionDto = {
  property: string;
  show: boolean;
  values: string[];
};

export type FilterFilesRequest = {
  projectId: number;
  filters: FilterConditionDto[];
};

export type PermissionDto = {
  id: string;
  description: string;
  name: string;
  code: string;
  ruleMapping: string;
  groupType: string;
  priority: number;
};

export type UserDto = {
  name: string;
  icon: string;
  id: string;
  email: string;
};

export type DocumentDto = {
  fileId: string;
  fileCode: string;
  name: string;
  type: string;
  jsonContent: string;
  isPublish: boolean;
  parentId: string;
  version: string;
  size: number;
  createDate: string;
  lastModified: string;
  owner: UserDto;
  id: string;
  fileType: number;
  cloudLink: string;
  bucketName: string;
};

export type LabelDto = {
  name: string;
  color: string;
  type: string;
  labelCode: string;
  id: string;
  createUser: UserDto;
  createdDate: string;
  lastModified: string;
  users: UserDto[];
  clientId: string;
  labelChildren: string[];
  documentChildren: DocumentDto[];
  fileType: number;
  isRootOrganization?: boolean;
};

export type SharedItemDto = {
  id: string;
  userId: string;
  permissions: PermissionDto[];
  entityType: string;
  document?: DocumentDto;
  label?: LabelDto;
  ownerId: string;
  isRootOrganization?: boolean;
};

export type SharedItemsResponse = {
  results: SharedItemDto[];
  page: number;
  pageCount: number;
  pageSize: number;
  queryCount: number;
  firstRowIndex: number;
  lastRowIndex: number;
};

export interface DeletedFilesApiResponse {
  results: DocumentDto[];
  page: number;
  pageCount: number;
  pageSize: number;
  queryCount: number;
  firstRowIndex: number;
  lastRowIndex: number;
}

export type UserStorageInfo = {
  userId: string;
  totalDocuments: number;
  storageSize: number;
  storageSizeFormatted: string;
  totalSubFolders: number;
  storageSizeGB: number;
  totalUsers: number;
  calculatedAt: string;
};

class FilesService extends ApiReduxHelpers {
  constructor() {
    super();
  }

  ApiHost = APIHosts.App;
  SearchApiHost = APIHosts.Upload;

  private envHeaders() {
    const env =
      (import.meta as any).env?.VITE_INERTIA_ENV ||
      localStorage.getItem("inertiaEnv") ||
      "NonProd";
    const profile =
      (import.meta as any).env?.VITE_INERTIA_PROFILE ||
      localStorage.getItem("inertiaProfile") ||
      "315";
    return {
      "X-Inertia-Env": env,
      ...(profile ? { "X-Inertia-Profile": profile } : {}),
    };
  }

  listProjectFiles(
    projectId: number,
    page: number,
    pageSize: number
  ): Observable<FileApiResponse> {
    localStorage.setItem("lastPage", page.toString());
    localStorage.setItem("lastPageSize", pageSize.toString());
    return HttpClient.get<FileApiResponse>(
      `${this.getHost(
        this.ApiHost
      )}/api/projects/${projectId}/files?page=${page}&pageSize=${pageSize}`,
      {
        headers: this.envHeaders(),
      }
    );
  }

  initFile(
    projectId: number,
    body: InitFileRequest
  ): Observable<InitFileResponse> {
    return HttpClient.post(
      `${this.getHost(this.ApiHost)}/api/projects/${projectId}/files`,
      body,
      {
        headers: this.envHeaders(),
      }
    );
  }

  completeVersion(
    fileId: number,
    body: CompleteVersionRequest
  ): Observable<CompleteVersionResponse> {
    return HttpClient.post(
      `${this.getHost(this.ApiHost)}/api/files/${fileId}/versions/complete`,
      body,
      {
        headers: this.envHeaders(),
      }
    );
  }

  getFilteredFiles(
    body: FilterFilesRequest,
    page: number,
    pageSize: number
  ): Observable<FileApiResponse> {
    localStorage.setItem("lastPage", page.toString());
    localStorage.setItem("lastPageSize", pageSize.toString());
    return HttpClient.post<FileApiResponse>(
      `${this.getHost(
        this.ApiHost
      )}/api/files/filtered?page=${page}&pageSize=${pageSize}`,
      body,
      {
        headers: this.envHeaders(),
      }
    );
  }

  getSearchFiles(
    companyId: number,
    projectId: string,
    contentKey?: string,
    fileNameKey?: string
  ): Observable<FileApiResponse> {
    let url = `${this.getHost(
      this.SearchApiHost
    )}/api/Search/search?companyId=${encodeURIComponent(
      companyId
    )}&projectId=${encodeURIComponent(projectId)}`;
    if (contentKey) {
      url += `&contentKey=${encodeURIComponent(contentKey)}`;
    }
    if (fileNameKey) {
      url += `&fileNameKey=${encodeURIComponent(fileNameKey)}`;
    }
    return HttpClient.get<FileApiResponse>(url, {
      headers: this.envHeaders(),
    });
  }

  checkFileExists(
    projectId: string,
    fileName: string,
    parentId?: string,
    companyId?: string
  ): Observable<FileExistsResponse> {
    const effectiveCompanyId = companyId || localStorage.getItem("companyId") || "1";
    let url = `${this.getHost(
      APIHosts.Upload
    )}/api/Upload/check-db?companyId=${encodeURIComponent(effectiveCompanyId)}&project=${projectId}&fileName=${encodeURIComponent(
      fileName
    )}`;

    // Thêm parentId nếu có
    if (parentId) {
      url += `&parentId=${encodeURIComponent(parentId)}`;
    }

    return HttpClient.get<FileExistsResponse>(url);
  }

  getFileVersions(fileId: string): Observable<FileVersionDto[]> {
    return HttpClient.get<FileVersionDto[]>(
      `${this.getHost(APIHosts.Upload)}/Document/${fileId}/version`,
      {
        headers: this.envHeaders(),
      }
    );
  }

  setDocumentActive(documentId: string, isActive: boolean): Observable<any> {
    return HttpClient.patch(
      `${this.getHost(APIHosts.Upload)}/Document/${documentId}/active`,
      { isActive },
      {
        headers: this.envHeaders(),
      }
    );
  }
  getSharedItems(
    page: number = 1,
    pageSize: number = 20
  ): Observable<SharedItemsResponse> {
    return HttpClient.get<SharedItemsResponse>(
      `${this.getHost(
        APIHosts.Upload
      )}/MappingPermission/shared-items?page=${page}&pageSize=${pageSize}`,
      {
        headers: this.envHeaders(),
      }
    );
  }
  getTeamItems(
    page: number = 1,
    pageSize: number = 20
  ): Observable<SharedItemsResponse> {
    return HttpClient.get<SharedItemsResponse>(
      `${this.getHost(
        APIHosts.Upload
      )}/MappingPermission/org/shared-items?page=${page}&pageSize=${pageSize}&isOrganization=true`,
      {
        headers: this.envHeaders(),
      }
    );
  }

  getTeamDrivesFolder(
    folderId: string,
    page: number = 1,
    pageSize: number = 20
  ): Observable<SharedItemsResponse> {
    return HttpClient.get<SharedItemsResponse>(
      `${this.getHost(
        APIHosts.Upload
      )}/Label/list/team-drives/${folderId}?isRootLabel=true&page=${page}&pageSize=${pageSize}`,
      {
        headers: this.envHeaders(),
      }
    );
  }
  getDeletedFiles(
    userId: string,
    page: number = 1,
    pageSize: number = 20
  ): Observable<DeletedFilesApiResponse> {
    return HttpClient.get<DeletedFilesApiResponse>(
      `${this.getHost(
        APIHosts.Upload
      )}/Document/deleted/user/${userId}?page=${page}&pageSize=${pageSize}`
    );
  }

  getDownloadUrl(
    objectKey: string,
    bucketName: string,
    documentId: string
  ): Observable<{ url: string }> {
    const url = `${this.getHost(
      APIHosts.Upload
    )}/api/Download/GetPresignedUrl?documentId=${encodeURIComponent(
      documentId
    )}&objectKey=${encodeURIComponent(
      objectKey
    )}&bucketName=${encodeURIComponent(bucketName)}&forceDownload=true`;

    return HttpClient.get<{ url: string }>(url, {
      headers: this.envHeaders(),
    });
  }

  viewPDF(
    objectKey: string,
    bucketName: string,
    documentId: string
  ): Observable<{ url: string }> {
    const url = `${this.getHost(
      APIHosts.Upload
    )}/api/Download/GetPresignedUrl?documentId=${encodeURIComponent(
      documentId
    )}&objectKey=${encodeURIComponent(
      objectKey
    )}&bucketName=${encodeURIComponent(bucketName)}&forceDownload=false`;

    return HttpClient.get<{ url: string }>(url, {
      headers: this.envHeaders(),
    });
  }

  getUserStorageInfo(userId: string, companyId: string): Observable<UserStorageInfo> {
    return HttpClient.get<UserStorageInfo>(
      `${this.getHost(APIHosts.Upload)}/Drive/user/${userId}/company/${companyId}`,
      {
        headers: this.envHeaders(),
      }
    );
  }

  duplicateDocument(
    sourceDocumentIds: string[],
    destinationFolderId: string,
    companyId: string,
    newDocumentName?: string
  ): Observable<any> {
    return HttpClient.post(
      `${this.getHost(
        APIHosts.Upload
      )}/Document/multi/duplicate?companyId=${encodeURIComponent(companyId)}`,
      {
        sourceDocumentIds,
        destinationFolderId,
        ...(newDocumentName && { newDocumentName }),
      },
      {
        headers: this.envHeaders(),
      }
    );
  }

  duplicateFolder(
    labelIds: string[]
  ): Observable<any> {
    return HttpClient.post(
      `${this.getHost(
        APIHosts.Upload
      )}/Label/duplicate`,
      labelIds,
      {
        headers: this.envHeaders(),
      }
    );
  }

  createFileShortcut(
    targetFileId: string,
    destinationFolderId: string
  ): Observable<any> {
    console.debug("[files.service] createFileShortcut: requesting", { targetFileId, destinationFolderId });
    return HttpClient.post(
      `${this.getHost(APIHosts.Upload)}/Document/drive-shortcut`,
      {
        TargetFileId: targetFileId,
        DestinationFolderId: destinationFolderId,
      },
      {
        headers: this.envHeaders(),
      }
    );
  }

  createFolderShortcut(
    targetLabelId: string,
    destinationFolderId: string
  ): Observable<any> {
    console.debug("[files.service] createFolderShortcut: requesting", { targetLabelId, destinationFolderId });
    return HttpClient.post(
      `${this.getHost(APIHosts.Upload)}/Label/drive-shortcut`,
      {
        TargetLabelId: targetLabelId,
        DestinationFolderId: destinationFolderId,
      },
      {
        headers: this.envHeaders(),
      }
    );
  }

  // Resolve-then-view for a shortcut row (D4/OQ7): resolves the target server-side and
  // returns a presigned URL to the target's own content, never the shortcut's own (null) CloudLink.
  openShortcut(
    id: string,
    forceDownload: boolean = false
  ): Observable<{ url: string }> {
    const url = `${this.getHost(
      APIHosts.Upload
    )}/Document/${encodeURIComponent(id)}/open?forceDownload=${forceDownload}`;

    return HttpClient.get<{ url: string }>(url, {
      headers: this.envHeaders(),
    });
  }
}

const filesService = new FilesService();
export default filesService;
